<template>
  <div class="order-detail">
    <!-- 加载状态 -->
    <div v-if="state.loading" class="loading-container">
      <van-loading type="spinner" color="#1989fa">加载中...</van-loading>
    </div>

    <!-- 订单内容 -->
    <div v-else-if="state.detail" class="detail-content">
      <!-- 状态卡片 -->
      <div class="status-card">
        <div class="status-left">
          <div class="status-icon-bg">
            <van-icon name="info" class="status-icon" />
          </div>
        </div>
        <div class="status-right">
          <div class="status-tag">
            {{ state.detail.statusDesc }}
          </div>
          <div class="status-desc">{{ getStatusDesc(state.detail) }}</div>
        </div>
      </div>

      <!-- 订单信息 -->
      <div class="order-info-card">
        <div class="info-row">
          <span class="info-label">订单号</span>
          <span class="info-value gray">{{ state.detail.orderNo }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">下单时间</span>
          <span class="info-value">{{ state.detail.createTime || '-' }}</span>
        </div>
      </div>

      <!-- 配送信息 -->
      <div class="section-card">
        <div class="section-header">
          <van-icon name="location-o" class="section-icon" />
          <span class="section-title">配送信息</span>
        </div>
        <div class="section-body">
          <div class="info-row">
            <span class="info-label">配送地址</span>
            <span class="info-value">{{ state.detail.deliveryAddress || '-' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">送餐日期</span>
            <span class="info-value">{{ formatDateTime(state.detail.plannedDiningTime) }}</span>
          </div>
        </div>
      </div>

      <!-- 餐食信息 -->
      <div class="section-card">
        <div class="section-header">
          <van-icon name="coupon-o" class="section-icon" />
          <span class="section-title">餐食信息</span>
        </div>
        <div class="section-body">
          <div class="info-row">
            <span class="info-label">套餐名称</span>
            <span class="info-value primary">{{ state.detail.mealPackage || '-' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">餐次</span>
            <span class="info-value">{{ formatMealPeriod(state.detail.mealPeriod) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">份数</span>
            <span class="info-value">× {{ state.detail.quantity || 0 }} 份</span>
          </div>
        </div>
      </div>

      <!-- 支付信息 -->
      <div class="section-card">
        <div class="section-header">
          <van-icon name="paid" class="section-icon" />
          <span class="section-title">支付信息</span>
        </div>
        <div class="section-body">
          <div class="info-row">
            <span class="info-label">支付金额</span>
            <span class="info-value price">¥{{ state.detail.payMoney || '0' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">支付状态</span>
            <span class="info-value" :class="getPayStatusClass(state.detail)">
              {{ getPayStatusText(state.detail) }}
            </span>
          </div>
          <div class="info-row" v-if="state.detail.payTime">
            <span class="info-label">支付时间</span>
            <span class="info-value">{{ state.detail.payTime }}</span>
          </div>
        </div>
      </div>

      <!-- 备注信息 -->
      <div class="section-card" v-if="state.detail.remark">
        <div class="section-header">
          <van-icon name="comment-o" class="section-icon" />
          <span class="section-title">备注</span>
        </div>
        <div class="remark-content">{{ state.detail.remark }}</div>
      </div>

      <!-- 底部操作按钮 -->
      <div class="bottom-actions" v-if="showActions">
        <div class="action-row">
          <van-button
            v-if="state.detail.payStatus == 7 && canOperateOrderDetail()"
            type="default"
            plain
            round
            class="action-btn secondary-btn"
            @click="handleCancel"
          >取消订单</van-button>
          <van-button
            v-if="state.detail.payStatus == 7 && canOperateOrderDetail()"
            type="primary"
            block
            round
            class="action-btn primary-btn"
            @click="handlePay"
          >立即支付 <span class="pay-amount">¥{{ state.detail.payMoney || '0' }}</span></van-button>
          <van-button
            v-if="state.detail.payStatus == 2 && state.detail.status == 0 && canOperateOrderDetail()"
            type="default"
            plain
            round
            class="action-btn secondary-btn"
            @click="handleRefund"
          >申请退款</van-button>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else class="empty-container">
      <van-empty description="订单不存在" />
    </div>
  </div>
</template>

<script>
import { defineComponent, reactive, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { showDialog, showNotify } from 'vant'
import { getMealReservationDetail, refundOrder, cancelMealReservation, payMealReservation } from '@/api/meal'
import { postSyparamSelectList } from "@/api/report";
import dayjs from 'dayjs'

export default defineComponent({
  name: 'CustomerMealReservationDetail',

  setup() {
    const route = useRoute()
    const router = useRouter()
    const state = reactive({
      loading: true,
      detail: null,
      userId: null,
      openId: null,
      lunchTPlus: 0,
      lunchBigDesc: '09:00',
      dinnerTPlus: 0,
      dinnerBigDesc: '15:00'
    })

    // 获取订单详情
    const getDetail = async () => {
      try {
        state.loading = true
        const orderNo = route.query.orderNo
        if (!orderNo) {
          showNotify({ type: 'warning', message: '缺少订单号参数' })
          state.loading = false
          return
        }

        const result = await getMealReservationDetail(orderNo)
        state.loading = false
        if (result.code === '00' && result.data && result.data.list && result.data.list.length > 0) {
          state.detail = result.data.list[0]
        } else {
          state.detail = null
        }
      } catch (error) {
        state.loading = false
        console.error('获取订单详情失败:', error)
        showNotify({ type: 'danger', message: '获取订单详情失败' })
      }
    }

    // 返回上一页
    const goBack = () => {
      router.back()
    }

    // 格式化日期时间
    const formatDateTime = (date) => {
      if (!date) return '-'
      return dayjs(date).format('YYYY-MM-DD')
    }

    // 格式化餐次
    const formatMealPeriod = (mealPeriod) => {
      const mealPeriodMap = {
        1: '早餐',
        2: '午餐',
        3: '晚餐',
        4: '夜宵'
      }
      return mealPeriodMap[mealPeriod] || '-'
    }

    // 获取状态文案
    const getStatusText = (item) => {
      const statusMap = {
        0: '待送餐',
        1: '已送餐',
        2: '已取消'
      }
      if (item.payStatus == 2 || !item.payStatus) {
        return statusMap[item.status]
      }
      return item.payStatusDesc || '待支付'
    }

    // 获取状态样式类名
    const getStatusClass = (item) => {
      if (item.payStatus == 2 || !item.payStatus) {
        return 'status-' + item.status
      }
      const classMap = {
        0: 'status-pending',
        1: 'status-paid',
        3: 'status-refunding',
        4: 'status-refunded',
        5: 'status-refund-fail',
        6: 'status-refunded',
        7: 'status-pending'
      }
      return classMap[item.payStatus] || 'status-pay'
    }

    // 获取支付状态样式
    const getPayStatusClass = (item) => {
      const classMap = {
        0: 'status-pending',
        1: 'status-paid',
        2: 'status-paid',
        3: 'status-refunding',
        4: 'status-refunded',
        5: 'status-refund-fail',
        6: 'status-refunded',
        7: 'status-pending'
      }
      return classMap[item.payStatus] || ''
    }

    // 获取状态描述
    const getStatusDesc = (item) => {
      if (item.payStatus == 7) {
        return '请尽快完成支付'
      }
      if (item.payStatus == 2 && item.status == 0) {
        return '餐食准备中，请耐心等待'
      }
      if (item.payStatus == 2 && item.status == 1) {
        return '餐食已送达'
      }
      if (item.payStatus == 2 && item.status == 2) {
        return '订单已取消'
      }
      return ''
    }

    // 获取支付状态文案
    const getPayStatusText = (item) => {
      const payStatusMap = {
        0: '待支付',
        1: '已支付',
        2: '已支付',
        3: '退费中',
        4: '已退款',
        5: '退款失败',
        6: '已退款',
        7: '待支付'
      }
      return item.payStatusDesc || payStatusMap[item.payStatus] || '待支付'
    }

    // 是否显示操作按钮
    const showActions = computed(() => {
      if (!state.detail) return false
      return state.detail.payStatus == 7 || (state.detail.payStatus == 2 && state.detail.status == 0)
    })

    // 判断是否允许操作订单（申请退款、取消订单、支付按钮是否显示）- 与列表页一致
    const canOperateOrderDetail = () => {
      if (!state.detail) return false
      const now = dayjs();
      const today = now.startOf('day');
      const plannedDate = dayjs(state.detail.plannedDiningTime).startOf('day');
      const diffDays = plannedDate.diff(today, 'day');

      // 午餐：limitDate = 今天 + lunchTPlus 天
      if (state.detail.mealPeriod == 2) {
        const tPlus = state.lunchTPlus;
        const [hour, minute] = state.lunchBigDesc.split(':').map(Number);
        const limitDate = today.add(tPlus, "day");
        const limitTime = today.add(hour, 'hour').add(minute, 'minute');

        console.log("午餐判断:", {
          今天: today.format("YYYY-MM-DD"),
          选择日期: plannedDate.format("YYYY-MM-DD"),
          diffDays,
          tPlus,
          limitDate: limitDate.format("YYYY-MM-DD"),
          limitTime: limitTime.format("YYYY-MM-DD HH:mm"),
          now: now.format("YYYY-MM-DD HH:mm"),
          isBeforeLimit: now.isBefore(limitTime)
        });


        if (diffDays < tPlus) {
          return false;
        }
        if (diffDays == tPlus) {
          return now.isBefore(limitTime);
        }
        return true;
      } else if (state.detail.mealPeriod == 3) {
        // 晚餐：limitDate = 今天 + dinnerTPlus 天
        const tPlus = state.dinnerTPlus;
        const [hour, minute] = state.dinnerBigDesc.split(':').map(Number);
        const limitDate = today.add(tPlus, "day");
        const limitTime = today.add(hour, 'hour').add(minute, 'minute');

        console.log("晚餐判断:", {
          今天: today.format("YYYY-MM-DD"),
          选择日期: plannedDate.format("YYYY-MM-DD"),
          diffDays,
          tPlus,
          limitDate: limitDate.format("YYYY-MM-DD"),
          limitTime: limitTime.format("YYYY-MM-DD HH:mm"),
          now: now.format("YYYY-MM-DD HH:mm"),
          isBeforeLimit: now.isBefore(limitTime)
        });

        if (diffDays < tPlus) {
          return false;
        }
        if (diffDays == tPlus) {
          return now.isBefore(limitTime);
        }
        return true;
      }
      return true;
    }


    // 判断是否允许退款/取消（基于餐次和截止时间）
    const canRefundOrCancel = (item) => {
      const now = dayjs();
      const plannedDate = dayjs(item.plannedDiningTime).startOf('day');
      
      // 午餐：截止时间为当天09:00
      if (item.mealPeriod == 2) {
        const deadline = plannedDate.add(9, 'hour');
        if (now.isAfter(deadline)) {
          showNotify({ type: 'warning', message: '已超过午餐退款截止时间（当天09:00）' });
          return false;
        }
      }
      // 晚餐：截止时间为当天15:00
      if (item.mealPeriod == 3) {
        const deadline = plannedDate.add(15, 'hour');
        if (now.isAfter(deadline)) {
          showNotify({ type: 'warning', message: '已超过晚餐退款截止时间（当天15:00）' });
          return false;
        }
      }
      return true;
    }

    // 支付
    const handlePay = () => {
      if (!state.detail) return
      showDialog({
        title: '确认支付',
        message: `确定要支付订单吗？金额：￥${state.detail.payMoney || '0'}`,
        showCancelButton: true
      }).then(async () => {
        try {
          const jsonData = JSON.stringify({
            ...state.detail,
            money: state.detail.payMoney
          })
          window.wx.miniProgram.navigateTo({ url: `/pages/payment/paymentResult?jsonData=${jsonData}` })
        } catch (error) {
          console.error('支付失败:', error)
          showNotify({ type: 'danger', message: '支付失败' })
        }
      }).catch(() => {
        console.log('取消')
      })
    }

    // 退款
    const handleRefund = () => {
      if (!state.detail) return

      if (!canRefundOrCancel(state.detail)) {
        return
      }
      showDialog({
        title: '确认退款',
        message: '确定要申请退款吗？',
        showCancelButton: true
      }).then(async () => {
        try {
          const result = await refundOrder({
            orderNo: state.detail.orderNo,
            userId: state.userId,
            openId: state.openId,
            refundAmount: Number(state.detail.payMoney) * 100
          })
          if (result.code === '00') {
            showNotify({ type: 'success', message: '退款申请已提交' })
            getDetail()
          } else {
            showNotify({ type: 'warning', message: result.message })
          }
        } catch (error) {
          console.error('退款失败:', error)
          showNotify({ type: 'danger', message: '退款失败' })
        }
      }).catch(() => {
        console.log('取消')
      })
    }

    // 取消订单
    const handleCancel = () => {
      if (!state.detail) return
      if (!canRefundOrCancel(state.detail)) {
        return
      }
      showDialog({
        title: '取消订单',
        message: '确定要取消该订单吗？',
        showCancelButton: true
      }).then(async () => {
        try {
          const result = await cancelMealReservation({
            id: state.detail.id
          })
          if (result.code === '00') {
            showNotify({ type: 'success', message: '取消订单成功' })
            getDetail()
          } else {
            showNotify({ type: 'warning', message: result.message })
          }
        } catch (error) {
          console.error('取消订单失败:', error)
          showNotify({ type: 'danger', message: '取消订单失败' })
        }
      }).catch(() => {
        console.log('取消')
      })
    }

    onMounted(() => {
      if (route.query.userId) {
        state.userId = route.query.userId
      }
      if (route.query.openId) {
        state.openId = route.query.openId
      }
      getDetail()
      getTimeLimit()
    })

    // 获取时间限制配置（与列表页一致）
    const getTimeLimit = async () => {
      try {
        const res = await postSyparamSelectList({ bigCode: "userPayTimeLimit" });
        if (res && Array.isArray(res.data)) {
          const orgData = res.data.filter(item => item.createOrg == route?.query?.mealOrgId);
          if (orgData.length === 0) {
            state.lunchTPlus = 0;
            state.lunchBigDesc = '09:00';
            state.dinnerTPlus = 0;
            state.dinnerBigDesc = '15:00';
          } else {
            const lunchItem = orgData.find(item => item.code == 2);
            const dinnerItem = orgData.find(item => item.code == 3);
            state.lunchTPlus = lunchItem ? parseInt(lunchItem.value) : 0;
            state.lunchBigDesc = lunchItem?.bigDesc ?? '09:00';
            state.dinnerTPlus = dinnerItem ? parseInt(dinnerItem.value) : 0;
            state.dinnerBigDesc = dinnerItem?.bigDesc ?? '15:00';
          }
        }
      } catch (e) {
        state.lunchTPlus = 0;
        state.lunchBigDesc = '09:00';
        state.dinnerTPlus = 0;
        state.dinnerBigDesc = '15:00';
      }
    };

    return {
      state,
      goBack,
      formatDateTime,
      formatMealPeriod,
      getStatusText,
      getStatusClass,
      getStatusDesc,
      getPayStatusText,
      getPayStatusClass,
      showActions,
      canOperateOrderDetail,
      handlePay,
      handleRefund,
      handleCancel
    }
  }
})
</script>

<style lang="scss" scoped>
.order-detail {
  min-height: 100vh;
  background: #f5f6f7;
  padding-bottom: 100px;

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 50vh;
  }

  .empty-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 50vh;
  }

  .detail-content {
    padding: 12px;

    .status-card {
      display: flex;
      align-items: center;
      padding: 24px 20px;
      background: linear-gradient(135deg, #1989fa 0%, #5eb0ff 100%);
      border-radius: 16px;
      margin-bottom: 12px;

      .status-left {
        .status-icon-bg {
          width: 56px;
          height: 56px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 16px;

          .status-icon {
            font-size: 28px;
            color: #fff;
          }
        }
      }

      .status-right {
        .status-tag {
          font-size: 22px;
          font-weight: 600;
          color: #fff;
          margin-bottom: 8px;
          letter-spacing: 1px;
        }

        .status-desc {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.85);
        }
      }
    }

    .order-info-card {
      background: #fff;
      border-radius: 12px;
      padding: 14px 16px;
      margin-bottom: 12px;
    }

    .section-card {
      background: #fff;
      border-radius: 12px;
      margin-bottom: 12px;
      overflow: hidden;

      .section-header {
        display: flex;
        align-items: center;
        padding: 14px 16px;
        border-bottom: 1px solid #f0f0f0;

        .section-icon {
          font-size: 18px;
          color: #1989fa;
          margin-right: 8px;
        }

        .section-title {
          font-size: 15px;
          font-weight: 600;
          color: #333;
        }
      }

      .section-body {
        padding: 4px 0;
      }
    }

    .info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;

      .info-label {
        font-size: 14px;
        color: #999;
        flex-shrink: 0;
      }

      .info-value {
        font-size: 14px;
        color: #333;
        text-align: right;
        margin-left: 20px;
        word-break: break-all;

        &.gray {
          color: #666;
          font-size: 13px;
        }

        &.primary {
          color: #1989fa;
          font-weight: 500;
        }

        &.price {
          color: #ff4d4f;
          font-weight: 600;
          font-size: 17px;
        }

        &.status-pending { color: #ff9600; }
        &.status-paid { color: #07c160; }
        &.status-refunding { color: #1989fa; }
        &.status-refunded { color: #999; }
        &.status-refund-fail { color: #f44; }
      }
    }

    .remark-content {
      padding: 12px 16px;
      font-size: 14px;
      color: #666;
      line-height: 1.6;
      background: #fafafa;
    }

    .bottom-actions {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 12px 16px;
      padding-bottom: calc(12px + env(safe-area-inset-bottom));
      background: linear-gradient(to top, rgba(255, 255, 255, 1) 80%, rgba(255, 255, 255, 0.95));
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.06);

      .action-row {
        display: flex;
        gap: 10px;
        align-items: center;
        justify-content: flex-end;
      }

      .action-btn {
        height: 44px;
        font-size: 15px;
        font-weight: 500;
        border-radius: 22px;
        transition: all 0.2s ease;
      }

      .primary-btn {
        flex: 1;
        background: linear-gradient(135deg, #1989fa 0%, #4da6ff 100%) !important;
        border: none !important;
        color: #fff !important;
        box-shadow: 0 4px 14px rgba(25, 137, 250, 0.35);

        .pay-amount {
          font-weight: 600;
          margin-left: 4px;
        }

        &:active {
          transform: scale(0.98);
          box-shadow: 0 2px 8px rgba(25, 137, 250, 0.25);
        }
      }

      .secondary-btn {
        flex: 0 0 auto;
        min-width: 90px;
        background: #fff !important;
        color: #666 !important;
        border: 1.5px solid #e8e8e8 !important;

        &:active {
          background: #f5f5f5 !important;
          border-color: #d0d0d0 !important;
        }
      }
    }
  }
}
</style>
